import { MarkerClusterer } from '@googlemaps/markerclusterer';

const sharedCluster = {
  instance: null,
  regularMarkers: [],
  photoMarkers: [],
  circleMarkers: [],
  lineMarkers: [],
  polygonMarkers: [],
};

export const customRenderer = {
  render({ count, position }) {
    const div = document.createElement('div');
    div.className = 'custom-cluster';
    div.textContent = count;
    return new google.maps.marker.AdvancedMarkerElement({
      position,
      content: div,
    });
  },
};

export const rebuildCluster = (mapReference) => {
  const allMarkers = [
    ...sharedCluster.regularMarkers,
    ...sharedCluster.photoMarkers,
    ...sharedCluster.circleMarkers,
    ...sharedCluster.lineMarkers,
    ...sharedCluster.polygonMarkers,
  ];

  if (sharedCluster.instance) {
    // Reuse the existing clusterer instead of destroying/recreating it. A new
    // MarkerClusterer only renders on its async onAdd/idle, so recreating left a
    // gap where markers were cleared but not yet redrawn — editing a marker (which
    // re-renders) made it vanish until the next idle (WEB-7053). clearMarkers(true)
    // removes without a redraw, then addMarkers renders synchronously.
    sharedCluster.instance.clearMarkers(true);
    sharedCluster.instance.addMarkers(allMarkers);
    return;
  }

  sharedCluster.instance = new MarkerClusterer({
    map: mapReference,
    markers: allMarkers,
    renderer: customRenderer,
  });

  // Sync shape visibility: when a marker is clustered its .map is null; when
  // visible it equals mapReference. Attached once — the instance is reused across
  // rebuilds, and the callback reads the live sharedCluster arrays each time.
  sharedCluster.instance.addListener('clusteringend', () => {
    const shapeMarkers = [
      ...sharedCluster.circleMarkers,
      ...sharedCluster.lineMarkers,
      ...sharedCluster.polygonMarkers,
    ];
    shapeMarkers.forEach(marker => {
      if (marker._shape) {
        marker._shape.setMap(marker.map ? mapReference : null);
      }
    });
  });
};

// Creates an invisible AdvancedMarkerElement at a given position for cluster representation.
// Returns null when the position has no finite lat/lng — a shape with missing/invalid geoJson
// coordinates would otherwise make AdvancedMarkerElement throw an uncaught InvalidValueError
// that crashes the whole map (WEB-7033). Callers must skip null results.
export const createInvisibleMarker = async (position) => {
  const lat = Number(position && position.lat);
  const lng = Number(position && position.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }
  const { AdvancedMarkerElement } = await window.google.maps.importLibrary('marker');
  const dot = document.createElement('div');
  dot.style.cssText = 'width:0;height:0;';
  return new AdvancedMarkerElement({ position: { lat, lng }, content: dot });
};

export default sharedCluster;
